/**
 * The stats readout and the boost countdown bar. Writes to the DOM only when
 * the rendered text actually changes, so the 60fps render loop doesn't thrash
 * layout twenty times a second for an unchanged string.
 */

import { formatEggsPrecise, formatRate } from '../game/numbers';

export interface BoostStatus {
  name: string;
  /** What it multiplies, e.g. "×2" or "clicks ×20". */
  label: string;
  secondsLeft: number;
  totalSeconds: number;
}

export interface StatsView {
  update(eggs: number, eps: number, perClick: number, boosts: readonly BoostStatus[]): void;
}

export interface StatsElements {
  eggCount: HTMLElement;
  eps: HTMLElement;
  clickValue: HTMLElement;
  /** Host for the boost countdown bars (one per running boost). */
  boost: HTMLElement;
}

/** A text node that only writes when its formatted value changes. */
function cell(node: HTMLElement): (text: string) => void {
  let last: string | null = null;
  return (text) => {
    if (text === last) return;
    last = text;
    node.textContent = text;
  };
}

export function createStatsView(els: StatsElements): StatsView {
  const eggs = cell(els.eggCount);
  const eps = cell(els.eps);
  const perClick = cell(els.clickValue);
  let lastBoostVisible: boolean | null = null;
  // Bars are built once and reused; a slot's label and fill write on change.
  interface Bar {
    row: HTMLElement;
    label: (text: string) => void;
    fill: HTMLElement;
    lastPct: number;
  }
  const bars: Bar[] = [];
  const bar = (i: number): Bar => {
    let b = bars[i];
    if (b) return b;
    const row = document.createElement('div');
    row.className = 'boost__row';
    const label = document.createElement('span');
    label.className = 'boost__label';
    const track = document.createElement('div');
    track.className = 'boost__track';
    const fill = document.createElement('div');
    fill.className = 'boost__fill';
    track.append(fill);
    row.append(label, track);
    els.boost.append(row);
    b = { row, label: cell(label), fill, lastPct: -1 };
    bars[i] = b;
    return b;
  };

  return {
    update(eggCount, epsValue, perClickValue, boosts) {
      eggs(formatEggsPrecise(eggCount));
      eps(formatRate(epsValue));
      perClick(formatRate(perClickValue));

      const visible = boosts.length > 0;
      if (visible !== lastBoostVisible) {
        lastBoostVisible = visible;
        els.boost.hidden = !visible;
      }
      boosts.forEach((boost, i) => {
        const b = bar(i);
        b.row.hidden = false;
        b.label(`${boost.name} ${boost.label} · ${Math.ceil(boost.secondsLeft)}s`);
        const pct = Math.round((100 * boost.secondsLeft) / boost.totalSeconds);
        if (pct !== b.lastPct) {
          b.lastPct = pct;
          b.fill.style.width = `${pct}%`;
        }
      });
      for (let i = boosts.length; i < bars.length; i++) bars[i]!.row.hidden = true;
    },
  };
}
